
import { memo, useState, useRef, useCallback } from "react";
import { EnhancedBookCover } from "@/components/ui/enhanced-book-cover";
import { BundleMonthTag } from "@/components/BundleMonthTag";
import { BookHoverPreview } from "@/components/BookHoverPreview";
import { useIsMobile } from "@/hooks/use-mobile";
import type { Book } from "@/types/book";

interface BookCardProps {
  title: string;
  cover_image_url: string;
  rating?: number;
  year?: string;
  onClick?: () => void;
  hideRatings?: boolean;
  bundleStartDate?: string;
  bookId?: number;
  cacheVersion?: number;
  bundleTagType?: 'current' | 'expired' | 'upcoming';
  bundleId?: number;
  bundleTitle?: string | null;
  showNotifyMe?: boolean;
  subscriptionDaysLeft?: number;
  tags?: string[] | null;
  episodeNumber?: number;
  // Full book object for hover preview
  book?: Book;
  onPlay?: () => void;
  // Legacy props for compatibility
  isFree?: boolean;
  isBlurred?: boolean;
  isHighlighted?: boolean;
  isSoldOut?: boolean;
  isSelectable?: boolean;
}

const HOVER_DELAY = 1000; // ms before showing preview

export const BookCard = memo(function BookCard({
  title,
  cover_image_url,
  rating,
  onClick,
  bundleStartDate,
  bookId,
  cacheVersion,
  bundleTagType,
  bundleTitle,
  tags,
  episodeNumber,
  book,
  onPlay,
}: BookCardProps) {
  const isMobile = useIsMobile();
  const cardRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

  const handleMouseEnter = useCallback(() => {
    if (isMobile || !book) return;
    
    hoverTimeoutRef.current = setTimeout(() => {
      if (cardRef.current) {
        setAnchorRect(cardRef.current.getBoundingClientRect());
        setShowPreview(true);
      }
    }, HOVER_DELAY);
  }, [isMobile, book]);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    // Close preview immediately when leaving the card (if not already hovering the preview)
    // The preview handles its own onMouseLeave
  }, []);

  const closePreview = useCallback(() => {
    setShowPreview(false);
    setAnchorRect(null);
  }, []);

  const handleClick = useCallback(() => {
    closePreview();
    onClick?.();
  }, [onClick, closePreview]);

  const handlePlay = useCallback(() => {
    closePreview();
    if (onPlay) {
      onPlay();
    } else {
      onClick?.();
    }
  }, [onPlay, onClick, closePreview]);

  return (
    <>
      <div 
        ref={cardRef}
        className="group cursor-pointer" 
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="relative aspect-[2/3] rounded-[8px] overflow-hidden shadow-lg transition-all duration-300 border-2 border-transparent lg:hover:border-white">
          <EnhancedBookCover
            src={cover_image_url}
            alt={title}
            className="w-full h-full"
            bookId={bookId}
            cacheVersion={cacheVersion}
            showSkeleton={true}
          />
          
          {/* Bundle Tag */}
          {bundleTagType && bundleStartDate && (
            <BundleMonthTag 
              startDate={bundleStartDate} 
              tagType={bundleTagType}
              bundleTitle={bundleTitle}
            />
          )}
          
          {/* Episode Number Badge */}
          {episodeNumber && (
            <div className="absolute top-2 left-2 z-10">
              <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-black/90 text-white rounded">
                EP {episodeNumber}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Hover Preview - Desktop Only */}
      {showPreview && anchorRect && book && (
        <BookHoverPreview
          book={book}
          anchorRect={anchorRect}
          onClose={closePreview}
          onOpenDialog={handleClick}
          onPlay={handlePlay}
        />
      )}
    </>
  );
});
